import { Moon, Sun, User, LogOut, Settings, UserCircle, Menu, Radio, MapPin, MapPinOff, Headphones, RefreshCw, GraduationCap, BriefcaseBusiness, FolderOpen } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useAuth } from '@/contexts/AuthContext';
import { useRBAC } from '@/contexts/RBACContext';
import { navDisplayName } from '@/lib/navDisplayName';
import { useNavigateWithParams } from '@/hooks/useNavigateWithParams';
import { useState, useMemo } from 'react';
import { useLiveDemoContext } from '@/contexts/LiveDemoContext';
import { useUserLocationContext } from '@/contexts/UserLocationContext';
import { currentSupabaseEnv } from '@/lib/supabase';
import { cache } from '@/lib/cache';
import { SupportFormModal } from '@/components/support/SupportFormModal';
import { toast } from 'react-toastify';
import { NotificationBell } from './NotificationBell';
import { UniversalSearch } from '@/components/search/UniversalSearch';
import { CollabLogo } from './CollabLogo';
import { MobileNavMenu } from './MobileNavMenu';
import { useHireCapabilities } from '@/hooks/useHireCapabilities';
import { useDownloadsUnreadCount } from '@/hooks/useDownloadsUnreadCount';
import { useClientPortalAccess } from '@/hooks/useClientPortalAccess';

interface HeaderProps {
  onSearchClick?: () => void;
  unitCount?: number;
  totalUnits?: number;
  mobileTitle?: string;
  /** Extra controls rendered in the mobile menu header, between the logo and the close button */
  mobileMenuActions?: React.ReactNode;
}

// Minimal tooltip component
function MiniTooltip({ children, text }: { children: React.ReactNode; text: string }) {
  const [show, setShow] = useState(false);

  return (
    <div className="relative" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-foreground bg-popover border border-border rounded shadow-md whitespace-nowrap z-50 pointer-events-none">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-border" />
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-popover" />
        </div>
      )}
    </div>
  );
}

export function Header({ onSearchClick: _onSearchClick, unitCount, totalUnits, mobileTitle, mobileMenuActions }: HeaderProps) {
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const isMobile = useIsMobile();
  const { signOut } = useAuth();
  const {
    refreshRBAC,
    isInternal,
    isSuperadmin,
    isAdmin,
    isCompanyAdmin,
    isGroupAdmin,
    profile,
    user,
    companies,
  } = useRBAC();
  // Universal search returns nothing to a plain member (the org scope ignores
  // plain memberships), so only staff and admins get the search control.
  const canSearch = isInternal || isSuperadmin || isAdmin || isCompanyAdmin || isGroupAdmin;
  const navigate = useNavigateWithParams();
  const location = useLocation();
  const hireCapabilities = useHireCapabilities();
  // "New documents" count for the Downloads badge — client (external) users only.
  const downloadsUnread = useDownloadsUnreadCount(!isInternal);
  // "Client portal" in the user menu: staff, company members, or a booking-grant client.
  const showClientPortal = useClientPortalAccess({ isInternal, companies, signedIn: !!user });
  const { isLiveDemoEnabled, toggleLiveDemo } = useLiveDemoContext();
  const { hasLocation, permissionStatus, requestPermission, loading: locationLoading } = useUserLocationContext();

  // Determine active navigation state
  const isActiveDashboard = location.pathname === '/' || location.pathname === '/dashboard';
  const isActiveMap = location.pathname === '/map';
  const isActiveReports = location.pathname === '/reports';
  const isActiveRecords = location.pathname.startsWith('/records');
  const isActiveAlerts = location.pathname === '/alerts';
  const isActiveDownloads = location.pathname.startsWith('/portal');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [supportModalOpen, setSupportModalOpen] = useState(false);

  // Demo environment detection - hostname-based OR Supabase env (for localhost)
  // Shows badge on: demo.hollywoodtrucks.com OR localhost when connected to demo Supabase
  const isDemoEnvironment = typeof window !== 'undefined' && (
    window.location.hostname === 'demo.hollywoodtrucks.com' ||
    currentSupabaseEnv === 'demo'
  );

  // Derive user name and company name from RBAC context
  // `profile` (UserProfile) carries no email — that lives on the RBAC `user` — so
  // hand it over explicitly or the blank-name email fallback can never fire.
  const userName = navDisplayName(profile ? { ...profile, email: user?.email } : null);
  const companyName = (() => {
    if (!companies || companies.length === 0) return null;
    const defaultCompanyId = profile?.default_company_id;
    const selectedCompany = defaultCompanyId
      ? companies.find((c) => c.id === defaultCompanyId) || companies[0]
      : companies[0];
    return selectedCompany?.name ?? null;
  })();

  // Get custom header logo if enabled for the user's company
  const customHeaderLogo = useMemo(() => {
    if (!companies || companies.length === 0) return null;
    const defaultCompanyId = profile?.default_company_id;
    const userCompany = defaultCompanyId
      ? companies.find((c) => c.id === defaultCompanyId) || companies[0]
      : companies[0];

    // Check if custom logo is enabled
    if (!userCompany?.theme_json?.use_custom_logo) return null;

    // Return appropriate logo based on dark mode
    // Fall back to the other variant if preferred is not available
    if (isDarkMode) {
      return userCompany.theme_json.logo_dark_url || userCompany.theme_json.logo_light_url || null;
    } else {
      return userCompany.theme_json.logo_light_url || userCompany.theme_json.logo_dark_url || null;
    }
  }, [companies, profile?.default_company_id, isDarkMode]);

  // Get logo display mode: 'replace' (default) or 'collab' (HT x Company)
  const logoDisplayMode = useMemo(() => {
    if (!companies || companies.length === 0) return 'replace' as const;
    const defaultCompanyId = profile?.default_company_id;
    const userCompany = defaultCompanyId
      ? companies.find((c) => c.id === defaultCompanyId) || companies[0]
      : companies[0];
    return userCompany?.theme_json?.logo_display_mode || 'replace';
  }, [companies, profile?.default_company_id]);

  // Get custom icon for mobile header (compact brand mark)
  const customHeaderIcon = useMemo(() => {
    if (!companies || companies.length === 0) return null;
    const defaultCompanyId = profile?.default_company_id;
    const userCompany = defaultCompanyId
      ? companies.find((c) => c.id === defaultCompanyId) || companies[0]
      : companies[0];
    if (!userCompany?.theme_json?.use_custom_logo) return null;
    if (isDarkMode) {
      return userCompany.theme_json.icon_dark_url || userCompany.theme_json.icon_light_url || null;
    } else {
      return userCompany.theme_json.icon_light_url || userCompany.theme_json.icon_dark_url || null;
    }
  }, [companies, profile?.default_company_id, isDarkMode]);

  const handleSignOut = async () => {
    await signOut();
    // Don't preserve params when logging out
    window.location.href = '/login';
  };

  /**
   * Clear all caches and refresh session data.
   * Useful when filters or permissions seem incorrect.
   */
  const handleRefreshSession = async () => {
    try {
      // 1. Clear IndexedDB cache
      await cache.clear();
      console.log('[Header] Cleared IndexedDB cache');

      // 2. Clear all localStorage filter/settings caches
      const keysToRemove = Object.keys(localStorage).filter(key =>
        key.startsWith('company_filter_') ||
        key.startsWith('settings_filters_') ||
        key.startsWith('ht_') ||
        key.includes('_cache')
      );
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log('[Header] Cleared localStorage caches:', keysToRemove.length, 'keys');

      // 3. Refresh RBAC data from server
      await refreshRBAC();
      console.log('[Header] Refreshed RBAC data');

      toast.success('Session refreshed! Filters and permissions reloaded.');

      // 4. Reload the page to ensure all contexts reinitialize
      window.location.reload();
    } catch (error) {
      console.error('[Header] Error refreshing session:', error);
      toast.error('Failed to refresh session. Please try signing out and back in.');
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <header className="relative border-b border-border bg-background dark:bg-background light-header md:z-auto z-20">
      <div className={`flex h-14 items-center justify-between ${isMobile ? 'px-2 gap-1.5' : 'px-6 gap-4'}`}>
        {/* Mobile: Hamburger Menu + Logo */}
        {isMobile ? (
          <div className="flex items-center gap-1 flex-shrink-0">
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0 flex flex-col h-full overflow-hidden">
                <SheetHeader className="px-4 pt-4 pb-3 border-b border-border">
                  {/* Single row with HT logo, optional menu actions and location button - pr-12 leaves room for X button */}
                  <div className="flex items-center justify-between gap-2 pr-12">
                    <SheetTitle className="flex items-center m-0 h-8">
                      {logoDisplayMode === 'collab' && (customHeaderIcon || customHeaderLogo) ? (
                        <CollabLogo
                          partnerLogoUrl={customHeaderIcon || customHeaderLogo || ''}
                          partnerName={companyName || 'Partner'}
                          isDarkMode={isDarkMode}
                          size="sm"
                        />
                      ) : customHeaderIcon ? (
                        <img src={customHeaderIcon} alt={companyName || 'Company'} className="h-7 w-auto max-w-[48px] object-contain" />
                      ) : (
                        <span className="text-2xl font-bold text-[#7ED957]">HT</span>
                      )}
                    </SheetTitle>
                    {/* Date range + reset controls, relocated from the dashboard status bar */}
                    {mobileMenuActions && (
                      <div className="ml-auto flex items-center">
                        {mobileMenuActions}
                      </div>
                    )}
                    {/* Location Permission Button - Only shown on mobile map page */}
                    {permissionStatus !== 'unsupported' && isActiveMap && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('[Header] Location button clicked');
                          requestPermission();
                        }}
                        disabled={locationLoading}
                        title={hasLocation ? 'Location enabled' : 'Enable location'}
                        className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-accent transition-colors relative flex-shrink-0"
                      >
                        {hasLocation ? (
                          <MapPin className="h-5 w-5 text-[#8FB560]" />
                        ) : (
                          <MapPinOff className="h-5 w-5 text-muted-foreground" />
                        )}
                        {locationLoading && (
                          <span className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-md">
                            <span className="h-4 w-4 border-2 border-[#8FB560] border-t-transparent rounded-full animate-spin" />
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                </SheetHeader>
                <MobileNavMenu onNavigate={() => setMobileNavOpen(false)} downloadsUnreadCount={downloadsUnread} />
              </SheetContent>
            </Sheet>
            <button
              onClick={handleLogoClick}
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              {logoDisplayMode === 'collab' && (customHeaderIcon || customHeaderLogo) ? (
                <CollabLogo
                  partnerLogoUrl={customHeaderIcon || customHeaderLogo || ''}
                  partnerName={companyName || 'Partner'}
                  isDarkMode={isDarkMode}
                  size="sm"
                />
              ) : customHeaderIcon ? (
                <img src={customHeaderIcon} alt={companyName || 'Company'} className="h-7 w-auto max-w-[48px] object-contain" />
              ) : (
                <span className="text-2xl font-bold text-[#7ED957]">HT</span>
              )}
            </button>
          </div>
        ) : (
          /* Desktop: Logo with optional Demo badge */
          <div className="flex items-center gap-2">
            <button
              onClick={handleLogoClick}
              className="flex items-center gap-3 flex-shrink-0 hover:opacity-80 transition-opacity"
            >
              {customHeaderLogo && logoDisplayMode === 'collab' ? (
                <CollabLogo
                  partnerLogoUrl={customHeaderLogo}
                  partnerName={companyName || 'Partner'}
                  isDarkMode={isDarkMode}
                />
              ) : customHeaderLogo ? (
                <img src={customHeaderLogo} alt={companyName || 'Company'} className="h-12 w-auto max-w-[200px] object-contain" />
              ) : (
                <>
                  <img src="/assets/ht_logo_white.png" alt="Hollywood Trucks" className="h-8 w-auto dark:hidden" />
                  <img src="/assets/ht-logo.png" alt="Hollywood Trucks" className="h-8 w-auto hidden dark:block" />
                </>
              )}
            </button>
            {isDemoEnvironment && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                Demo
              </span>
            )}
          </div>
        )}

        {/* Mobile: Title or Unit Count (Centered) */}
        {isMobile && (mobileTitle || unitCount !== undefined) && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-medium uppercase tracking-wider flex items-center gap-1 whitespace-nowrap pointer-events-none">
            {mobileTitle ? (
              <span className="text-foreground font-bold text-sm normal-case tracking-tight">{mobileTitle}</span>
            ) : totalUnits && unitCount !== undefined && unitCount < totalUnits ? (
              <>
                <span className="text-[#8FB560] font-bold">{unitCount}</span>
                <span className="text-muted-foreground">/</span>
                <span className="text-muted-foreground">{totalUnits}</span>
                <span className="text-muted-foreground">UNITS</span>
              </>
            ) : unitCount !== undefined ? (
              <span className="text-muted-foreground">{unitCount} UNITS</span>
            ) : null}
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Navigation Links - Desktop only, centered */}
        {!isMobile && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center text-sm font-medium uppercase gap-6 lg:gap-8 xl:gap-10">
            <button
              onClick={() => navigate('/dashboard')}
              className={`transition-colors ${
                isActiveDashboard
                  ? 'text-[#8FB560]'
                  : 'hover:text-[#8FB560]'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => navigate('/map')}
              className={`transition-colors ${
                isActiveMap
                  ? 'text-[#8FB560]'
                  : 'hover:text-[#8FB560]'
              }`}
            >
              Map
            </button>
            <button
              onClick={() => navigate('/reports')}
              className={`transition-colors ${
                isActiveReports
                  ? 'text-[#8FB560]'
                  : 'hover:text-[#8FB560]'
              }`}
            >
              Reports
            </button>
            {!isInternal && (
              <button
                onClick={() => navigate('/portal')}
                className={`relative transition-colors ${
                  isActiveDownloads
                    ? 'text-[#8FB560]'
                    : 'hover:text-[#8FB560]'
                }`}
              >
                Downloads
                {downloadsUnread > 0 && (
                  <span
                    className="absolute -top-1.5 -right-2.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-error/80 px-1 text-[10px] font-bold text-white"
                    aria-label={`${downloadsUnread} new documents`}
                  >
                    {downloadsUnread > 99 ? '99+' : downloadsUnread}
                  </span>
                )}
              </button>
            )}
            {isInternal && (
              <button
                onClick={() => navigate('/records')}
                className={`transition-colors ${
                  isActiveRecords
                    ? 'text-[#8FB560]'
                    : 'hover:text-[#8FB560]'
                }`}
              >
                Records
              </button>
            )}
            {isInternal && (
              <button
                onClick={() => navigate('/alerts')}
                className={`transition-colors ${
                  isActiveAlerts
                    ? 'text-[#8FB560]'
                    : 'hover:text-[#8FB560]'
                }`}
              >
                Alerts
              </button>
            )}
          </div>
        )}

        {/* Right Icons */}
        <div className={`flex items-center flex-shrink-0 ${isMobile ? 'gap-1.5' : 'gap-3'}`}>
          {/* User Name and Company - Desktop only */}
          {!isMobile && userName && (
            <div className="flex flex-col items-end mr-2">
              <span className="font-medium leading-tight text-sm">{userName}</span>
              {companyName && (
                <span className="uppercase tracking-wider light-header-company leading-tight text-[10px]">
                  {companyName}
                </span>
              )}
            </div>
          )}

          {/* Live Demo Toggle - Mobile only, demo env only (drives
              simulated data; prod must never expose it) */}
          {isMobile && currentSupabaseEnv === 'demo' && (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 relative"
              onClick={toggleLiveDemo}
              title={isLiveDemoEnabled ? 'Live updates on' : 'Live updates off'}
            >
              <Radio className={`h-5 w-5 transition-colors ${isLiveDemoEnabled ? 'text-[#8FB560]' : ''}`} />
              {isLiveDemoEnabled && (
                <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[#8FB560] animate-pulse" />
              )}
            </Button>
          )}

          {/* Universal search (⌘K / Ctrl-K) - staff and admins */}
          {canSearch && <UniversalSearch />}

          {/* Notification Bell - Superadmin only */}
          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {/* Mobile: User name and company at top */}
              {isMobile && userName && (
                <>
                  <div className="px-2 py-2">
                    <div className="font-medium text-sm">{userName}</div>
                    {companyName && (
                      <div className="text-xs uppercase tracking-wider text-[#8FB560]">{companyName}</div>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                </>
              )}
              {/* Header with Account text and toggle icons */}
              <div className="flex items-center justify-between px-2 py-1.5">
                <DropdownMenuLabel className="p-0">Account</DropdownMenuLabel>
                <div className="flex items-center gap-1">
                  {/* Dark mode toggle */}
                  <MiniTooltip text={isDarkMode ? 'Dark mode' : 'Light mode'}>
                    <button
                      onClick={toggleDarkMode}
                      className="h-8 w-8 rounded-md hover:bg-accent transition-colors flex items-center justify-center"
                    >
                      {isDarkMode ? (
                        <Moon className="h-4 w-4 fill-current" />
                      ) : (
                        <Sun className="h-4 w-4" />
                      )}
                    </button>
                  </MiniTooltip>
                </div>
              </div>
              <DropdownMenuSeparator />

              {/* Menu items */}
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings/profile')}>
                <UserCircle className="mr-2 h-4 w-4" />
                <span>My Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/training')}>
                <GraduationCap className="mr-2 h-4 w-4" />
                <span>Training</span>
              </DropdownMenuItem>
              {showClientPortal && (
                <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/portal')}>
                  <FolderOpen className="mr-2 h-4 w-4" />
                  <span>Client portal</span>
                </DropdownMenuItem>
              )}

              {hireCapabilities.canView && (
                <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/hire')}>
                  <BriefcaseBusiness className="mr-2 h-4 w-4" />
                  <span>Hiring</span>
                </DropdownMenuItem>
              )}

              {/* Platform features - TBD, hidden for now */}
              {/* {!isLoadingAdmin && isSuperadmin && (
                <>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings/messages')}>
                    <MessageSquare className="mr-2 h-4 w-4" />
                    <span>Messages</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings/invoices')}>
                    <FileText className="mr-2 h-4 w-4" />
                    <span>Invoices</span>
                  </DropdownMenuItem>
                </>
              )} */}

              <DropdownMenuItem className="cursor-pointer" onClick={() => setSupportModalOpen(true)}>
                <Headphones className="mr-2 h-4 w-4" />
                <span>Support</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings & Admin</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleRefreshSession} className="cursor-pointer flex items-center justify-between text-muted-foreground hover:text-foreground">
                <span>Refresh Session</span>
                <RefreshCw className="h-4 w-4" />
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer flex items-center justify-between text-[#8FB560] dark:text-[#8FB560]">
                <span>Sign out</span>
                <LogOut className="h-4 w-4" />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Support Modal */}
      <SupportFormModal
        open={supportModalOpen}
        onOpenChange={setSupportModalOpen}
      />
    </header>
  );
}
